import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { me, setGuestId } from 'utils/authApi'

export type Role = 'client' | 'guest' | 'anonymous'

export type AuthClaims =
  | { clientId: string; role: 'client' }
  | { clientId: string; role: 'guest' }

type AuthContextValue = {
  clientId?: string
  confirmed: boolean
  role: Role
  setAuth: (claims: AuthClaims | null) => void
  promoteToGuest: (clientId: string) => void
}

const AuthContext = createContext<AuthContextValue>({
  clientId: undefined,
  confirmed: false,
  role: 'anonymous',
  setAuth: () => {},
  promoteToGuest: () => {},
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [clientId, setClientId] = useState<string>()
  const [role, setRole] = useState<Role>('anonymous')
  const confirmed = Boolean(clientId)

  useEffect(() => {
    const init = async () => {
      try {
        const data = await me()
        setClientId(data.clientId ?? data.guestId ?? undefined)
        setRole(data.role)
      } catch (err) {
        console.error('[AuthContext] Error fetching identity:', err)
      }
    }
    init()
  }, [])

  const setAuth = (claims: AuthClaims | null) => { 
    setClientId(claims?.clientId)
    setRole(claims?.role ?? 'anonymous')

    if (claims?.role === 'guest') {
      setGuestId(claims.clientId)
    }
  }

  const promoteToGuest = (clientId: string) => {
    if (role !== 'anonymous') return
    console.log('promoting to guest!')
    setAuth({ role: 'guest', clientId })
  }

  return (
    <AuthContext.Provider value={{ clientId, role, confirmed, setAuth, promoteToGuest }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
